import { cx, useSwipe } from "@jugl-web/utils";
import { FC, ReactNode } from "react";
import { PlainButton } from "../../primitives/PlainButton";
import { ReactComponent as CloseIcon } from "./icons/close.svg";
import { SwipeableHeaderProps } from "./types";

export const SwipeableHeader: FC<SwipeableHeaderProps> = ({
  title,
  subtitle,
  startSlot,
  hasHandle = true,
  hasCloseButton = true,
  customEndSlot,
  isDetached,
  showOnlyEndSlot,
  className,
  onClose,
  onSwipeUp,
}) => (
  <header
    className={cx(
      "relative flex items-center justify-between px-4 pt-[30px] pb-5",
      className
    )}
  >
    {isDetached && (
      <div className="absolute inset-x-4 bottom-0 h-px bg-[#F6F6F6]" />
    )}
    {hasHandle && (
      <SwipeableArea onSwipeDown={onClose} onSwipeUp={onSwipeUp}>
        <SwipeableHandle />
      </SwipeableArea>
    )}
    {!showOnlyEndSlot && (
      <div className="flex items-center gap-4 overflow-hidden">
        {startSlot}
        <div className="flex flex-col gap-0.5">
          <span className="truncate text-sm leading-[140%] font-medium tracking-[0.14px] text-dark">
            {title}
          </span>
          {subtitle && (
            <span className="text-xs leading-[140%] font-normal text-dark-600">
              {subtitle}
            </span>
          )}
        </div>
      </div>
    )}
    {customEndSlot ||
      (hasCloseButton && (
        <PlainButton onClick={onClose} className="relative">
          <CloseIcon />
        </PlainButton>
      ))}
  </header>
);

interface SwipeableAreaProps {
  children: ReactNode;
  className?: string;
  onSwipeDown: () => void;
  onSwipeUp?: () => void;
}

export const SwipeableArea: FC<SwipeableAreaProps> = ({
  children,
  className,
  onSwipeDown,
  onSwipeUp,
}) => {
  const { touchProps } = useSwipe({
    onSwipeDown,
    onSwipeUp,
  });

  return (
    <div
      className={cx("absolute inset-x-0 top-0 h-10", className)}
      {...touchProps}
    >
      {children}
    </div>
  );
};

interface SwipeableHandleProps {
  className?: string;
}

export const SwipeableHandle: FC<SwipeableHandleProps> = ({ className }) => (
  <div
    className={cx(
      "absolute top-4 left-1/2 h-1 w-14 -translate-x-1/2 rounded-sm bg-[#F2F2F2]",
      className
    )}
  />
);
